import Widget from "../models/widget.js";
import UniqueIdGenerator from "../utils/uniqueIdGenerator.js";
import InternalEvent from "../../core/constants/internalEvent.js";
import InternalEventBus from "../../core/utils/internalEventBus.js";
import widgetType from '../constants/widgetType.js';

var DataService = require('./data-service/data-service.js');

const numberOfExtraData = 10;

class WidgetService {
  constructor() {
    this.widgets = {};
    this.dataSetMap = {};
    this.addWidgetType = null;
    this.previousUserAction = {};
  }

  addComponentToWidget(widgetId, componentController, parameters) {
    this.requestGetSnapshotDataForComponent(widgetId, componentController);
    this.widgets[widgetId].addComponent(componentController, parameters);
  }

  requestGetSnapshotDataForComponent(widgetId, componentController) {
    let historySize = componentController.getHistorySize();
    const widget = this.widgets[widgetId];
    const requestHistorySize = historySize === 1 ? 1 : historySize + numberOfExtraData;
    const previousUserAction = this.previousUserAction[widgetId];
    const resolutionOption = this.getResolution(widget, historySize, previousUserAction);
    const requestParams = this.getRequestArguments(componentController, widgetId, resolutionOption);

    if (this.doesComponentRequireServerSideFunctions(componentController) && !widget.metaData.updateInterval) {
      const serverSideFunctionRequests = this.getServerSideFunctionRequests(componentController, widget.metaData.resolution);
      DataService.getServerSideFunctionResult(serverSideFunctionRequests, widget.metaData.resolution, historySize)
        .then(response => {
          if (this.widgets[widgetId]) {
            InternalEventBus.publish(InternalEvent.Dashboard.WIDGET_SNAPSHOT_LOADED, {
              widgetId,
              noSubscriptions: response.noSubscriptions
            });
            this.widgets[widgetId].onComponentSnapshotObtained(componentController.id, response.result, requestHistorySize, true);
          }
        });
    } else {
      DataService.getLatestData(requestParams, requestHistorySize, resolutionOption, previousUserAction)
        .then(result => {
          if (this.widgets[widgetId]) {
            InternalEventBus.publish(InternalEvent.Dashboard.WIDGET_SNAPSHOT_LOADED, {
              widgetId,
              noSubscriptions: result.noSubscriptions
            });

            // we need to provide this indicator down to the chart instnace in odrder to set histoic mode in the chart
            // by default chart mode is realtime and user won't be able to slide chart to left
            if (widget.type === 'TIME_SERIES') {
              result.restored = !!previousUserAction;
            }
            this.widgets[widgetId].onComponentSnapshotObtained(componentController.id, result, requestHistorySize);
          }
        });
    }
  }

  getResolution(widget, windowSize, previousUserAction) {
    const resolution = widget.metaData.resolution;
    const maxRepeatDuration = parseInt(widget.metaData.maxRepeatDuration || 1);

    if (resolution && resolution[0] !== 'tick') {
      let endTime = Date.now();
      let startTime = endTime - (resolution[1] * 1000 * (windowSize + numberOfExtraData));  // default data loding

      if (previousUserAction && previousUserAction.value) {
        if (previousUserAction.value < Date.now()) {
          switch (previousUserAction.type) {
            case 'set_time': {
              const halfWindows = Math.round((windowSize + 1) / 2);
              const halfWindowTime = resolution[1] * 1000 * halfWindows;
              const otherHalfWindowTime = resolution[1] * 1000 * (windowSize + 1 - halfWindows);
              startTime = previousUserAction.value - halfWindowTime;
              endTime = previousUserAction.value + otherHalfWindowTime;
              break;
            }
            case 'move_left': {
              endTime = previousUserAction.value + resolution[1] * 1000;
              startTime = endTime - (resolution[1] * 1000 * (windowSize + 1));
              break;
            }
            case 'move_right': {
              startTime = previousUserAction.value - (previousUserAction.value % (resolution[1] * 1000));
              endTime = startTime + (resolution[1] * 1000 * (windowSize + 1));
              break;
            }
          }
        }
      }

      console.log('startTime' + ' ' + new Date(startTime));
      console.log('endTime' + ' ' + new Date(endTime));

      return { startTime: new Date(startTime), endTime: new Date(endTime), resolution, maxRepeatDuration };
    } else {
      return { maxRepeatDuration };
    }
  }

  getRequestArguments(componentControllerOrLegends, widgetId, { resolution }) {
    const datasets = [];
    const events = [];
    const legends = Array.isArray(componentControllerOrLegends) ? componentControllerOrLegends : componentControllerOrLegends.getLegends();
    legends.forEach(l => {
      for (let prop in l.mappings) {
        const datasetId = l.mappings[prop].datasetId;
        const fieldId = l.mappings[prop].fieldId;

        if (resolution && l.parameters.chartType === 'bubble') {
          if (!events.find(e => e.datasetId === datasetId)) {
            // this is a special case. Consider bubble chart with resolution as event data.
            // event data should not be summarized so using tick data.
            events.push({ datasetId, fieldId, id: `${widgetId}_${l.id}` });
          }
        } else if (resolution || !datasets.find(d => d.datasetId === datasetId)) {
          // Using tick mode, we get the entire data so we don't need to duplicated datasets
          // Using resolution mode, we only get requested field, so we need duplicated datasets
          datasets.push({ datasetId, fieldId, id: `${widgetId}_${l.id}`, chartType: l.parameters.chartType });
        }
      }
    });
    return {
      datasets,
      events
    };
  }

  doesComponentRequireServerSideFunctions(componentControllerOrLegends) {
    let hasServerSideFunction = false;
    const legends = Array.isArray(componentControllerOrLegends) ? componentControllerOrLegends : componentControllerOrLegends.getLegends();
    legends.forEach(l => {
      if (l.hasOwnProperty("serverSideFunction") && l.serverSideFunction.length > 0) {
        hasServerSideFunction = true;
      }
    });
    return hasServerSideFunction;
  }

  getServerSideFunctionRequests(componentControllerOrLegends, resolution) {
    const legends = Array.isArray(componentControllerOrLegends) ? componentControllerOrLegends : componentControllerOrLegends.getLegends();
    const requests = [];

    legends.forEach(l => {
      if (l.hasOwnProperty("serverSideFunction")) {
        if (!Array.isArray(l.serverSideFunction)) {
          l.serverSideFunction = [l.serverSideFunction];
        }
        l.serverSideFunction.map(sf => {
          requests.push({
            name: sf.name,
            requestId: sf.requestId,
            params: sf.params,
            isEventData: l.parameters.chartType === 'bubble' && resolution && resolution[0] !== 'tick'
          });
        });
      }
    });
    return requests;
  }

  /**
   * Creating snapshot(only left side) data request for functions
   */
  getFunctionRequestsForSnapshotData(legends, resolution, options) {
    const isTickMode = resolution && resolution[0] === 'tick';
    const requests = [];

    legends.forEach((l, i) => {
      l.serverSideFunction.forEach(sf => {
        const p = sf.params;
        if (isTickMode) {
          // Convert TSCalculate -> GetDataByRecordLocation
          const { fileBlockIndex, fileBlockRecordIndex, numberOfDataPoints } = options[i];
          const FORWARD_EXCLUSIVE = 0;

          requests.push({
            name: 'GetDataByRecordLocation',
            requestId: sf.requestId,
            params: [p[0], p[1], fileBlockIndex, fileBlockRecordIndex, FORWARD_EXCLUSIVE, numberOfDataPoints + functionBufferSize]
          });
        } else {
          if (l.parameters.chartType === 'bubble') {
            // Event type chart(need ticks instead of resolution data)
            // Convert TSCalculate -> GetDataForTimeRange
            const { startTime, numberOfDataPoints } = options[0];   // request period must be based on primary dataset(non-event dataset)
            const nextStartTime = startTime + resolution[1] * 1000;
            let endTime = nextStartTime + (resolution[1] * 1000 * numberOfDataPoints);
            if (endTime > Date.now()) {
              endTime = Date.now();
            }
            requests.push({
              name: 'GetDataForTimeRange',
              requestId: sf.requestId,
              params: [
                p[0],
                p[1],
                new Date(startTime).toISOString(),
                new Date(endTime).toISOString()
              ],
              isEventData: true
            });
          } else {
            const { startTime, numberOfDataPoints } = options[i];
            const nextStartTime = startTime + resolution[1] * 1000;
            let endTime = nextStartTime + (resolution[1] * 1000 * numberOfDataPoints);
            if (endTime > Date.now()) {
              endTime = Date.now();
            }
            requests.push({
              name: 'TSCalculate',
              requestId: sf.requestId,
              params: [
                p[0],
                p[1],
                new Date(nextStartTime).toISOString(),
                new Date(endTime).toISOString(),
                resolution[3],
                resolution[2],
                7
              ]
            });
          }
        }
      });
    });

    return requests;
  }

  registerToDataStreamMap(newWidget) {
    newWidget.legends.map(legend => {
      for (let key in legend.mappings) {
        let datasetId = legend.mappings[key].datasetId;
        this.dataSetMap[datasetId] = this.dataSetMap[datasetId] || {};
        this.dataSetMap[datasetId][newWidget.id] = newWidget;
      }
    })
  }

  deregisterFromDataStreamMap(widgetComponent) {
    for (let datasetId in this.dataSetMap) {
      let widgets = this.dataSetMap[datasetId];
      for (let widgetId in widgets) {
        let widget = widgets[widgetId];
        if (widget.belongingComponent === widgetComponent) {
          delete this.widgets[widgetId];
          if (widgets.length === 0) {
            delete this.dataSetMap[datasetId];
          }
        }
      }
    }
  }

  removeAllWidgetsBelongsTo(widgetComponent) {
    for (let widgetId in this.widgets) {
      let widget = this.widgets[widgetId];
      if (widget.belongingComponent === widgetComponent) {
        delete this.widgets[widgetId];
        delete this.previousUserAction[widgetId];
      }
    }
  }

  removeFromDataStreamMap(widgetId) {
    var deletedDataStreams = [];
    for (let datasetId in this.dataSetMap) {
      let widgetsForDataStream = this.dataSetMap[datasetId];
      if (widgetsForDataStream.hasOwnProperty(widgetId)) {
        delete widgetsForDataStream[widgetId];
        if (jQuery.isEmptyObject(widgetsForDataStream)) {
          deletedDataStreams.push(datasetId);
          delete this.dataSetMap[datasetId];
        }
      }
    }

    return deletedDataStreams;
  }

  addDataStreamsToMap(widget, legends) {
    legends.map(legend => {
      let datasetId = legend.datasetId;
      if (!this.dataSetMap.hasOwnProperty(datasetId)) {
        this.dataSetMap[datasetId] = {}
      }
      this.dataSetMap[datasetId][widget.id] = widget;
    });
  }

  removeWidgetFromDataStreamsMap(widget, datasetIds) {
    datasetIds.map(datasetId => {
      let widgetsForDataStreamId = this.dataSetMap[datasetId];
      delete widgetsForDataStreamId[widget.id];
      if (jQuery.isEmptyObject(widgetsForDataStreamId)) {
        delete this.dataSetMap[datasetIds];
      }
    });
  }


  resetComponentsForWidge(widgetId) {
    this.widgets[widgetId].resetComponents();
  }

  createRealtimeWidget(payload) {
    let widgetId = payload.widgetId == null ? UniqueIdGenerator.id() : payload.widgetId;
    let newWidget = new Widget(widgetId,
      payload.type,
      payload.title,
      payload.legends,
      payload.belongingComponent,
      payload.metaData || {},
      payload.autoAdjustWindowSize,
      payload.centerZeroLine,
      payload.splitLeftRight);
    this.widgets[newWidget.id] = newWidget;
    this.registerToDataStreamMap(newWidget);
    this.addLegends(widgetId, payload.legends);

    return newWidget.clone(newWidget);
    // return newWidget;
  }

  createStudioChartWidget(payload) {
    let newWidget = new Widget(payload.widgetId,
      payload.type,
      payload.title,
      payload.legends,
      payload.belongingComponent,
      payload.metaData);
    this.widgets[newWidget.id] = newWidget;
    this.registerToDataStreamMap(newWidget);
    this.addLegends(payload.widgetId, payload.legends);

    return newWidget;
  }

  addLegends(widgetId, legends) {
    let widget = this.widgets[widgetId];
    widget.addLegends(legends);
    this.addDataStreamsToMap(widget, legends);

    if (widget.type !== "TRADING_VIEW") {
      DataService.subscribe(legends);
    }
  }

  addWidget(id, type, title, legends, belongingComponent, dashboardVariables) {
    let newWidget = new Widget(id, type, title, legends, belongingComponent);
    this.registerToDataStreamMap(newWidget);
    this.widgets[newWidget.id] = newWidget;
    return newWidget.clone(newWidget);
  }

  updateWidgetLegends(widgetId, legends) {
    if (this.widgets.hasOwnProperty(widgetId)) {
      this.widgets[widgetId].updateLegends(legends);
    }
  }

  updateWidgetMetaData(widgetId, metaData) {
    this.widgets[widgetId].updateMetaData(metaData);
  }

  removeLegendsForWidget(widgetId, legends) {
    let widget = this.widgets[widgetId];
    let deletedDataStreams = widget.removeLegends(legends);
    this.removeWidgetFromDataStreamsMap(widget, deletedDataStreams);
    let disposableDataStreams = [];

    deletedDataStreams.map(datasetId => {
      if (this.dataSetMap[datasetId] == null) {
        disposableDataStreams.push(datasetId);
      }
    });

    DataService.unsubscribe(disposableDataStreams);

    return disposableDataStreams;
  }

  removeWidget(widgetId) {
    let deletedDataStreams = this.removeFromDataStreamMap(widgetId);
    if (this.widgets[widgetId]) {
      this.widgets[widgetId].destroy();
      delete this.widgets[widgetId];
    }
    DataService.unsubscribe(deletedDataStreams);
    return deletedDataStreams;
  }

  getWidget(widgetId) {
    let widgetCopy = null;
    let widget = this.widgets[widgetId];
    if (widget) {
      widgetCopy = Object.assign({}, widget);
    }
    return widgetCopy;
  }

  reset(widgetComponent) {
    this.removeAllWidgetsBelongsTo(widgetComponent);
    this.deregisterFromDataStreamMap(widgetComponent);
  };

  setAddWidgetType(type) {
    this.addWidgetType = type;
  }

  getAddWidgetType() {
    return this.addWidgetType;
  };

  setPreviousUserAction(widgetId, action) {
    this.previousUserAction[widgetId] = action;
  }

  removePreviousUserAction(widgetId) {
    delete this.previousUserAction[widgetId];
  }
}

const instance = new WidgetService();
export default instance;


// WEBPACK FOOTER //
// ./src/core/services/widgetService.js